/**
 * Automate — Layout route for the workflow orchestration engine
 *
 * 4 tabs: Workflows (default), Playbooks, Nodes, History
 * Tabs hidden on sub-pages (workflow builder, playbook detail).
 */

import { createFileRoute, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ContentHeader } from '@/components/content-header';
import { Workflow, BookOpen, History, Plus, Blocks } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CreateWorkflowDialog } from '@/components/automate/create-workflow-dialog';

export const Route = createFileRoute('/automate')({
  component: AutomateLayout,
  validateSearch: (search: Record<string, unknown>) => ({
    action: typeof search.action === 'string' ? search.action : undefined,
  }),
});

const TAB_PATHS = [
  '/automate', '/automate/',
  '/automate/playbooks', '/automate/playbooks/',
  '/automate/nodes', '/automate/nodes/',
  '/automate/history', '/automate/history/',
];

function AutomateLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const isTabPage = TAB_PATHS.includes(location.pathname);
  const [createOpen, setCreateOpen] = useState(false);

  // ?action=create deep-link opens the dialog (#102 — the URL was the
  // only side-effect of the menu item before; now it actually renders
  // the wizard).
  useEffect(() => {
    if (search.action === 'create') {
      setCreateOpen(true);
    }
  }, [search.action]);

  const closeCreateDialog = (open: boolean) => {
    setCreateOpen(open);
    if (!open && search.action === 'create') {
      // Drop the query string so a stale ?action=create doesn't reopen
      // the dialog on every nav.
      navigate({ to: '/automate', search: {} } as never);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {isTabPage && (
        <ContentHeader
          title="Automate"
          tabs={[
            { label: 'Workflows', path: '/automate', icon: Workflow },
            { label: 'Playbooks', path: '/automate/playbooks', icon: BookOpen },
            { label: 'Nodes', path: '/automate/nodes', icon: Blocks },
            { label: 'History', path: '/automate/history', icon: History },
          ]}
          actions={
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="gap-1.5" title="Create new automation entities">
                  <Plus className="h-3.5 w-3.5" /> New
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-xs"
                  onSelect={() => setCreateOpen(true)}
                >
                  <Workflow className="h-3.5 w-3.5 mr-2" /> New Workflow
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          }
        />
      )}
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
      <CreateWorkflowDialog open={createOpen} onOpenChange={closeCreateDialog} />
    </div>
  );
}
